import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Heading,
  VStack,
  HStack,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Text,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Center,
  Select,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Card,
  CardHeader,
  CardBody,
  Flex,
  IconButton,
  Grid,
  GridItem
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useFindMany, useGlobalAction, useSession } from "@gadgetinc/react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

// Custom hook to add timeout to data fetching hooks
function useWithTimeout(hookResult, timeoutMs = 5000, fallbackData = []) {
  const [timedOut, setTimedOut] = useState(false);
  const timeoutRef = useRef(null);
  
  const [result, refetch] = hookResult;
  const { data, fetching, error } = result;

  useEffect(() => {
    if (fetching && !timedOut) {
      timeoutRef.current = setTimeout(() => {
        console.error(`AdminApproval: API call timed out after ${timeoutMs}ms`);
        setTimedOut(true);
      }, timeoutMs);
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (!fetching) {
        setTimedOut(false);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [fetching, timeoutMs, timedOut]);

  // Return modified result with timeout handling
  return [
    {
      data: timedOut ? fallbackData : data,
      fetching: timedOut ? false : fetching,
      error: timedOut ? new Error('Request timed out') : error
    },
    () => {
      setTimedOut(false);
      refetch();
    }
  ];
}

export default function AdminUserApprovals() {
  const [domainFilter, setDomainFilter] = useState("");
  const [approving, setApproving] = useState(null);
  const [denying, setDenying] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [approvalForm, setApprovalForm] = useState({
    role: 'User',
    assistantAccess: 'specific',
    selectedAssistants: [],
    duration: '1_year',
    accessNotes: ''
  });
  const session = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Debug logging
  console.debug('AdminApproval: session=', session);
  console.debug('AdminApproval: user roles=', session?.user?.roles);

  // Simplified access control - just check if session exists and has admin role
  const isAdmin = session?.user?.roles?.some(role => 
    role.name === "system-admin" || role.name === "admin"
  );

  console.debug('AdminApproval: isAdmin=', isAdmin);

  // Data fetching hooks with timeout protection
  console.debug('AdminApproval: Setting up data fetching hooks');
  
  const pendingHookResult = useFindMany(api.user, {
    filter: {
      approvalStatus: { equals: "pending" },
      ...(domainFilter && { email: { contains: domainFilter } })
    },
    select: {
      id: true,
      email: true,
      createdAt: true,
      requestNotes: true,
      firstName: true,
      lastName: true
    },
    sort: { createdAt: "Descending" }
  });

  const approvedHookResult = useFindMany(api.user, {
    filter: {
      approvalStatus: { equals: "approved" },
      ...(domainFilter && { email: { contains: domainFilter } })
    },
    select: {
      id: true,
      email: true,
      createdAt: true,
      approvedAt: true,
      firstName: true,
      lastName: true
    },
    sort: { approvedAt: "Descending" }
  });

  const deniedHookResult = useFindMany(api.user, {
    filter: {
      approvalStatus: { equals: "denied" },
      ...(domainFilter && { email: { contains: domainFilter } })
    },
    select: {
      id: true,
      email: true,
      createdAt: true,
      deniedReason: true,
      firstName: true,
      lastName: true
    },
    sort: { createdAt: "Descending" }
  });

  // Fetch assistants for selection
  const assistantsHookResult = useFindMany(api.assistant, {
    select: {
      id: true,
      name: true,
      description: true
    },
    sort: { name: "Ascending" }
  });

  // Apply timeout wrapper to all hooks
  const [{ data: pendingUsers, fetching: pendingFetching, error: pendingError }, refetchPending] = 
    useWithTimeout(pendingHookResult, 5000, []);
  
  const [{ data: approvedUsers, fetching: approvedFetching, error: approvedError }, refetchApproved] = 
    useWithTimeout(approvedHookResult, 5000, []);
  
  const [{ data: deniedUsers, fetching: deniedFetching, error: deniedError }, refetchDenied] = 
    useWithTimeout(deniedHookResult, 5000, []);

  const [{ data: assistants, fetching: assistantsFetching, error: assistantsError }] = 
    useWithTimeout(assistantsHookResult, 5000, []);

  // Debug logging for data states
  if (pendingFetching) console.debug('AdminApproval: fetching pending users');
  if (approvedFetching) console.debug('AdminApproval: fetching approved users');
  if (deniedFetching) console.debug('AdminApproval: fetching denied users');
  
  if (pendingError) console.error('AdminApproval error loading pending users:', pendingError);
  if (approvedError) console.error('AdminApproval error loading approved users:', approvedError);
  if (deniedError) console.error('AdminApproval error loading denied users:', deniedError);

  // Action hooks
  const [, approveUser] = useGlobalAction(api.approveUser);
  const [, denyUser] = useGlobalAction(api.denyUser);

  // Simplified access control check
  if (!session) {
    console.debug('AdminApproval: No session found, showing auth required');
    return (
      <Box bg="#111111" minH="100vh" color="white" p={6}>
        <Alert status="error" bg="red.900" color="white">
          <AlertIcon />
          <AlertTitle>Authentication Required</AlertTitle>
          <AlertDescription>Please sign in to access this page.</AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (!isAdmin) {
    console.debug('AdminApproval: User not admin, showing access denied');
    return (
      <Box bg="#111111" minH="100vh" color="white" p={6}>
        <Alert status="error" bg="red.900" color="white">
          <AlertIcon />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>You must be an administrator to access this page.</AlertDescription>
        </Alert>
      </Box>
    );
  }

  const openApprovalModal = (user) => {
    setSelectedUser(user);
    // Reset form with defaults
    setApprovalForm({
      role: 'User',
      assistantAccess: 'specific',
      selectedAssistants: assistants?.find(a => a.name === 'MAiA')?.id ? [assistants.find(a => a.name === 'MAiA').id] : [],
      duration: '1_year',
      accessNotes: ''
    });
    onOpen();
  };

  const handleApprove = async () => {
    if (!selectedUser) return;
    
    console.debug('AdminApproval: Attempting to approve user:', selectedUser.id, 'with options:', approvalForm);
    try {
      setApproving(selectedUser.id);
      const result = await approveUser({ 
        userId: selectedUser.id,
        approvedBy: session.user.id,
        role: approvalForm.role,
        assistantAccess: approvalForm.assistantAccess,
        selectedAssistants: approvalForm.selectedAssistants,
        duration: approvalForm.duration,
        accessNotes: approvalForm.accessNotes
      });
      console.debug('AdminApproval: Approve result:', result);
      
      if (result.success) {
        toast({
          title: "User Approved",
          description: result.message || "User has been successfully approved.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        // Refetch with error handling
        try {
          refetchPending();
          refetchApproved();
        } catch (refetchError) {
          console.error('AdminApproval: Error refetching after approve:', refetchError);
        }
        onClose();
      } else {
        throw new Error(result.message || "Failed to approve user");
      }
    } catch (error) {
      console.error('AdminApproval: Error approving user:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to approve user. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setApproving(null);
    }
  };

  const handleDeny = async (userId, reason) => {
    console.debug('AdminApproval: Attempting to deny user:', userId, 'reason:', reason);
    try {
      setDenying(userId);
      const deniedReason = reason || "Account denied by administrator";
      const result = await denyUser({ userId, deniedReason });
      console.debug('AdminApproval: Deny result:', result);
      
      if (result.success) {
        toast({
          title: "User Denied",
          description: result.message || "User has been successfully denied.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        // Refetch with error handling
        try {
          refetchPending();
          refetchDenied();
        } catch (refetchError) {
          console.error('AdminApproval: Error refetching after deny:', refetchError);
        }
      } else {
        throw new Error(result.message || "Failed to deny user");
      }
    } catch (error) {
      console.error('AdminApproval: Error denying user:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to deny user. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDenying(null);
    }
  };

  const handleFormChange = (field, value) => {
    setApprovalForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAssistantToggle = (assistantId) => {
    setApprovalForm(prev => ({
      ...prev,
      selectedAssistants: prev.selectedAssistants.includes(assistantId)
        ? prev.selectedAssistants.filter(id => id !== assistantId)
        : [...prev.selectedAssistants, assistantId]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderUserTable = (users, status, fetching, error, showActions = false) => {
    console.debug(`AdminApproval: Rendering ${status} table - fetching:${fetching}, error:${!!error}, users:${users?.length || 0}`);
    
    if (fetching) {
      return (
        <Center py={8}>
          <VStack>
            <Spinner size="lg" />
            <Text fontSize="sm" color="gray.500">Loading {status} users...</Text>
          </VStack>
        </Center>
      );
    }

    if (error) {
      console.error(`AdminApproval: Error in ${status} table:`, error);
      return (
        <Alert status="error" mb={4}>
          <AlertIcon />
          <Box>
            <AlertTitle>Error Loading {status} Users</AlertTitle>
            <AlertDescription>
              {error.message || `Failed to load ${status} users. Please try refreshing the page.`}
            </AlertDescription>
          </Box>
        </Alert>
      );
    }

    // Ensure users is an array, fallback to empty array
    const safeUsers = Array.isArray(users) ? users : [];
    
    if (safeUsers.length === 0) {
      return (
        <Box textAlign="center" py={8}>
          <Text color="gray.500">
            No {status} users found.
          </Text>
          {status === 'pending' && (
            <Text fontSize="sm" color="gray.400" mt={2}>
              New user registrations will appear here for approval.
            </Text>
          )}
        </Box>
      );
    }

    return (
      <Table className="approval-table" variant="simple" size="sm" color="white">
        <Thead>
          <Tr>
            <Th color="gray.300">Name</Th>
            <Th color="gray.300">Email</Th>
            <Th color="gray.300">Registration Date</Th>
            {status === "pending" && <Th color="gray.300">Request Notes</Th>}
            {status === "approved" && <Th color="gray.300">Approved Date</Th>}
            {status === "denied" && <Th color="gray.300">Denial Reason</Th>}
            {showActions && <Th color="gray.300">Actions</Th>}
          </Tr>
        </Thead>
        <Tbody>
          {safeUsers.map((user) => (
            <Tr key={user.id}>
              <Td color="white">
                {user.firstName || user.lastName 
                  ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                  : "N/A"
                }
              </Td>
              <Td color="white">{user.email || "N/A"}</Td>
              <Td color="white">{user.createdAt ? formatDate(user.createdAt) : "N/A"}</Td>
              {status === "pending" && (
                <Td maxWidth="200px" color="white">
                  <Text fontSize="sm" isTruncated title={user.requestNotes} color="white">
                    {user.requestNotes || "No notes"}
                  </Text>
                </Td>
              )}
              {status === "approved" && (
                <Td color="white">{user.approvedAt ? formatDate(user.approvedAt) : "N/A"}</Td>
              )}
              {status === "denied" && (
                <Td maxWidth="200px" color="white">
                  <Text fontSize="sm" isTruncated title={user.deniedReason} color="white">
                    {user.deniedReason || "No reason provided"}
                  </Text>
                </Td>
              )}
              {showActions && (
                <Td color="white">
                  <HStack spacing={2}>
                    <Button
                      className="btn-approve"
                      size="sm"
                      bg="#3b82f6"
                      color="white"
                      _hover={{ bg: "#2563eb" }}
                      onClick={() => openApprovalModal(user)}
                      isLoading={approving === user.id}
                      loadingText="Approving..."
                      disabled={!user.id}
                    >
                      Approve
                    </Button>
                    <Button
                      className="btn-deny"
                      size="sm"
                      colorScheme="red"
                      variant="outline"
                      onClick={() => handleDeny(user.id, "Denied by administrator")}
                      isLoading={denying === user.id}
                      loadingText="Denying..."
                      disabled={!user.id}
                    >
                      Deny
                    </Button>
                  </HStack>
                </Td>
              )}
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  };

  // Safe count calculation with fallbacks
  const pendingCount = Array.isArray(pendingUsers) ? pendingUsers.length : 0;
  const approvedCount = Array.isArray(approvedUsers) ? approvedUsers.length : 0;
  const deniedCount = Array.isArray(deniedUsers) ? deniedUsers.length : 0;

  console.debug('AdminApproval: Counts - pending:', pendingCount, 'approved:', approvedCount, 'denied:', deniedCount);

  return (
    <Box bg="#111111" minH="100vh" color="white">
      <Box maxW="1200px" mx="auto" p={6}>
        <VStack spacing={6} align="stretch">
          {/* Header with Back Button */}
          <Flex align="center" justify="space-between" mb={4}>
            <HStack>
              <IconButton
                icon={<ArrowBackIcon />}
                onClick={() => navigate('/admin')}
                bg="#3b82f6"
                color="white"
                _hover={{ bg: "#2563eb" }}
                aria-label="Back to Admin Dashboard"
                size="sm"
              />
              <Box>
                <Heading as="h1" size="lg" color="white">
                  User Approval Management
                </Heading>
                <Text color="gray.400" fontSize="sm">
                  Manage user registration approval requests and view approval history
                </Text>
              </Box>
            </HStack>
          </Flex>

          {/* Filter Section */}
          <Card bg="#1a1a1a" borderColor="#333">
            <CardBody>
              <Input
                placeholder="Filter by email domain (e.g., @southwest.tn.edu)"
                value={domainFilter}
                onChange={(e) => setDomainFilter(e.target.value)}
                maxW="400px"
                bg="#2a2a2a"
                borderColor="#444"
                color="white"
                _placeholder={{ color: "gray.500" }}
                _focus={{ borderColor: "#3b82f6", boxShadow: "0 0 0 1px #3b82f6" }}
              />
            </CardBody>
          </Card>

          {/* Tabs */}
          <Tabs variant="enclosed" colorScheme="blue">
            <TabList bg="#1a1a1a" borderColor="#333">
              <Tab color="gray.400" _selected={{ color: "white", bg: "#3b82f6" }}>
                Pending Approval 
                {pendingCount > 0 && (
                  <Badge ml={2} bg="#f59e0b" color="black" borderRadius="full">
                    {pendingCount}
                  </Badge>
                )}
              </Tab>
              <Tab color="gray.400" _selected={{ color: "white", bg: "#3b82f6" }}>
                Approved Users
                {approvedCount > 0 && (
                  <Badge ml={2} bg="#10b981" color="white" borderRadius="full">
                    {approvedCount}
                  </Badge>
                )}
              </Tab>
              <Tab color="gray.400" _selected={{ color: "white", bg: "#3b82f6" }}>
                Denied Users
                {deniedCount > 0 && (
                  <Badge ml={2} bg="#ef4444" color="white" borderRadius="full">
                    {deniedCount}
                  </Badge>
                )}
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel px={0}>
                <Card bg="#1a1a1a" borderColor="#333">
                  <CardHeader>
                    <Heading as="h2" size="md" color="#f59e0b">
                      Pending Approval ({pendingCount})
                    </Heading>
                  </CardHeader>
                  <CardBody>
                    {renderUserTable(pendingUsers, "pending", pendingFetching, pendingError, true)}
                  </CardBody>
                </Card>
              </TabPanel>

              <TabPanel px={0}>
                <Card bg="#1a1a1a" borderColor="#333">
                  <CardHeader>
                    <Heading as="h2" size="md" color="#10b981">
                      Approved Users ({approvedCount})
                    </Heading>
                  </CardHeader>
                  <CardBody>
                    {renderUserTable(approvedUsers, "approved", approvedFetching, approvedError)}
                  </CardBody>
                </Card>
              </TabPanel>

              <TabPanel px={0}>
                <Card bg="#1a1a1a" borderColor="#333">
                  <CardHeader>
                    <Heading as="h2" size="md" color="#ef4444">
                      Denied Users ({deniedCount})
                    </Heading>
                  </CardHeader>
                  <CardBody>
                    {renderUserTable(deniedUsers, "denied", deniedFetching, deniedError)}
                  </CardBody>
                </Card>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>

        {/* Enhanced Approval Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent bg="#1a1a1a" color="white" borderColor="#333">
            <ModalHeader borderBottomColor="#333">
              Approve User: {selectedUser?.firstName} {selectedUser?.lastName}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={6} align="stretch">
                <Box>
                  <Text fontSize="sm" color="gray.400" mb={2}>User Details</Text>
                  <Box bg="#2a2a2a" p={3} borderRadius="md" borderColor="#444" border="1px">
                    <Text><strong>Name:</strong> {selectedUser?.firstName} {selectedUser?.lastName}</Text>
                    <Text><strong>Email:</strong> {selectedUser?.email}</Text>
                    <Text><strong>Registration Date:</strong> {selectedUser?.createdAt && formatDate(selectedUser.createdAt)}</Text>
                    {selectedUser?.requestNotes && (
                      <Text><strong>Notes:</strong> {selectedUser.requestNotes}</Text>
                    )}
                  </Box>
                </Box>

                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <Text fontSize="sm" color="gray.400" mb={2}>Role Assignment</Text>
                    <Select
                      value={approvalForm.role}
                      onChange={(e) => handleFormChange('role', e.target.value)}
                      bg="#2a2a2a"
                      borderColor="#444"
                      color="white"
                      _focus={{ borderColor: "#3b82f6" }}
                    >
                      <option value="User" style={{ background: '#2a2a2a' }}>User</option>
                      <option value="admin" style={{ background: '#2a2a2a' }}>Admin</option>
                      <option value="system-admin" style={{ background: '#2a2a2a' }}>System Admin</option>
                    </Select>
                  </GridItem>

                  <GridItem>
                    <Text fontSize="sm" color="gray.400" mb={2}>Access Duration</Text>
                    <Select
                      value={approvalForm.duration}
                      onChange={(e) => handleFormChange('duration', e.target.value)}
                      bg="#2a2a2a"
                      borderColor="#444"
                      color="white"
                      _focus={{ borderColor: "#3b82f6" }}
                    >
                      <option value="30_days" style={{ background: '#2a2a2a' }}>30 Days</option>
                      <option value="90_days" style={{ background: '#2a2a2a' }}>90 Days</option>
                      <option value="1_year" style={{ background: '#2a2a2a' }}>1 Year</option>
                      <option value="unlimited" style={{ background: '#2a2a2a' }}>Unlimited</option>
                    </Select>
                  </GridItem>
                </Grid>

                <Box>
                  <Text fontSize="sm" color="gray.400" mb={2}>Assistant Access</Text>
                  <Select
                    value={approvalForm.assistantAccess}
                    onChange={(e) => handleFormChange('assistantAccess', e.target.value)}
                    bg="#2a2a2a"
                    borderColor="#444"
                    color="white"
                    _focus={{ borderColor: "#3b82f6" }}
                    mb={3}
                  >
                    <option value="all" style={{ background: '#2a2a2a' }}>All Assistants</option>
                    <option value="specific" style={{ background: '#2a2a2a' }}>Specific Assistants</option>
                    <option value="none" style={{ background: '#2a2a2a' }}>No Assistant Access</option>
                  </Select>

                  {approvalForm.assistantAccess === 'specific' && (
                    <Box bg="#2a2a2a" p={3} borderRadius="md" borderColor="#444" border="1px">
                      <Text fontSize="sm" color="gray.400" mb={2}>Select Assistants:</Text>
                      {assistantsFetching ? (
                        <Center py={4}>
                          <Spinner size="sm" />
                        </Center>
                      ) : (
                        <VStack align="stretch" spacing={2}>
                          {Array.isArray(assistants) && assistants.map((assistant) => (
                            <HStack key={assistant.id} spacing={3}>
                              <input
                                type="checkbox"
                                id={`assistant-${assistant.id}`}
                                checked={approvalForm.selectedAssistants.includes(assistant.id)}
                                onChange={() => handleAssistantToggle(assistant.id)}
                                style={{ accentColor: '#3b82f6' }}
                              />
                              <Box flex={1}>
                                <Text fontSize="sm" fontWeight="medium">{assistant.name}</Text>
                                {assistant.description && (
                                  <Text fontSize="xs" color="gray.500">{assistant.description}</Text>
                                )}
                              </Box>
                            </HStack>
                          ))}
                        </VStack>
                      )}
                    </Box>
                  )}
                </Box>

                <Box>
                  <Text fontSize="sm" color="gray.400" mb={2}>Access Notes (Optional)</Text>
                  <Textarea
                    value={approvalForm.accessNotes}
                    onChange={(e) => handleFormChange('accessNotes', e.target.value)}
                    placeholder="Add any notes about this user's access permissions..."
                    bg="#2a2a2a"
                    borderColor="#444"
                    color="white"
                    _placeholder={{ color: "gray.500" }}
                    _focus={{ borderColor: "#3b82f6", boxShadow: "0 0 0 1px #3b82f6" }}
                    rows={3}
                  />
                </Box>
              </VStack>
            </ModalBody>

            <ModalFooter borderTopColor="#333">
              <HStack spacing={3}>
                <Button
                  variant="outline"
                  onClick={onClose}
                  borderColor="#444"
                  color="gray.400"
                  _hover={{ bg: "#2a2a2a", borderColor: "#555" }}
                >
                  Cancel
                </Button>
                <Button
                  bg="#3b82f6"
                  color="white"
                  _hover={{ bg: "#2563eb" }}
                  onClick={handleApprove}
                  isLoading={approving === selectedUser?.id}
                  loadingText="Approving..."
                  disabled={
                    approvalForm.assistantAccess === 'specific' && 
                    approvalForm.selectedAssistants.length === 0
                  }
                >
                  Approve User
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </Box>
  );
}